import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import CodeEditor from '../components/CodeEditor';
import TestRunner from '../components/TestRunner';
import TaskWorkLayout from '../components/TaskWorkLayout';
import MarkdownText from '../components/MarkdownText';
import TaskStatementMeta from '../components/TaskStatementMeta';
import StatementAttachmentLinks from '../components/StatementAttachmentLinks';
import { shouldCopyTablesAsSpaces } from '../utils/tableCopyAsSpaces';

export default function TaskDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHints, setShowHints] = useState(false);
  const [solutions, setSolutions] = useState([]);
  const [showSolutions, setShowSolutions] = useState(false);
  const [savingStatusId, setSavingStatusId] = useState(null);

  useEffect(() => {
    fetchTask();
  }, [slug]);

  async function fetchTask() {
    setLoading(true);
    try {
      const res = await api.get(`/tasks/${slug}/`);
      setTask(res.data);
      setCode(res.data.initial_code || res.data.template_code || '# Введите ваше решение здесь\n');
    } catch {
      setError('Задача не найдена');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSolutions() {
    try {
      const res = await api.get(`/tasks/${slug}/solutions/`);
      setSolutions(Array.isArray(res.data) ? res.data : res.data.results || []);
      setShowSolutions(true);
    } catch {
      setSolutions([]);
      setShowSolutions(true);
    }
  }

  async function updateSolutionStatus(solutionId, selfReportStatus) {
    if (!solutionId) return;
    setSavingStatusId(solutionId);
    try {
      await api.patch(`/tasks/solutions/${solutionId}/`, {
        self_report_status: selfReportStatus,
      });
      await fetchSolutions();
    } finally {
      setSavingStatusId(null);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Загрузка задачи...</p>
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="error-page">
        <h2>Ошибка</h2>
        <p>{error || 'Задача не найдена'}</p>
      </div>
    );
  }

  const visibleTests = (task.test_cases || []).filter((tc) => tc.is_visible !== false);
  const hintsEnabled = user?.editor_settings?.showHints === true;

  const header = (
    <>
      <div className="task-detail-header">
        <h1>{task.title}</h1>
        {task.difficulty && (
          <span className={`difficulty-badge difficulty-${task.difficulty}`}>
            {task.difficulty === 'easy' ? 'Лёгкая' :
             task.difficulty === 'medium' ? 'Средняя' : 'Сложная'}
          </span>
        )}
        <StatementAttachmentLinks attachments={task.statement_attachments} variant="detail" />
      </div>
      <TaskStatementMeta
        tags={task.tags}
        source_name={task.source_name}
        source_number={task.source_number}
        source_author={task.source_author}
        year={task.year}
      />
    </>
  );

  const statement = (
    <div className="task-statement">
      <MarkdownText
        className="statement-text"
        text={task.statement || task.description || task.text || 'Описание задачи'}
        copyTablesAsSpaces={shouldCopyTablesAsSpaces({
          source_name: task.source_name,
          slug: task.slug || slug,
        })}
      />
    </div>
  );

  const hintsSection = hintsEnabled && task.hints && task.hints.length > 0 ? (
    <div className="hints-section">
      <button
        className="btn btn-outline btn-sm"
        onClick={() => setShowHints(!showHints)}
      >
        {showHints ? '🙈 Скрыть подсказки' : '💡 Показать подсказки'}
      </button>
      {showHints && (
        <div className="hints-list">
          {task.hints.map((hint, i) => (
            <div key={i} className="hint-item">
              <strong>Подсказка {i + 1}:</strong> {typeof hint === 'string' ? hint : hint.text}
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  const solutionsSidebar = (
    <div className="solutions-section">
      <button
        className="btn btn-outline btn-sm"
        onClick={fetchSolutions}
      >
        📋 Мои решения
      </button>
      {showSolutions && (
        <div className="solutions-history">
          {solutions.length === 0 ? (
            <p className="text-secondary">Вы ещё не отправляли решений</p>
          ) : (
            solutions.map((sol, i) => (
              <div key={i} className="solution-item">
                <div className="solution-main-row">
                  <span className={`solution-status status-${sol.status}`}>
                    {sol.status === 'accepted' ? '✅' : '❌'} {sol.status}
                  </span>
                  <span className="solution-date">
                    {sol.submitted_at ? new Date(sol.submitted_at).toLocaleString('ru-RU') : ''}
                  </span>
                </div>
                <div className="solution-self-report-row">
                  <span className="solution-self-label">Оценка:</span>
                  {['EASY', 'OK_NO_PEEK', 'OK_WITH_PEEK', 'OK_WITH_QUESTION', 'FAILED', 'NO_IDEAS', 'NOT_TRIED'].map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`btn btn-xs ${sol.self_report_status === status ? 'btn-primary' : 'btn-outline'}`}
                      disabled={savingStatusId === sol.id}
                      onClick={() => updateSolutionStatus(sol.id, status)}
                      title={
                        status === 'EASY' ? '++' : status === 'OK_NO_PEEK' ? '+' : status === 'OK_WITH_PEEK' ? '+-' :
                        status === 'OK_WITH_QUESTION' ? '+?' : status === 'FAILED' ? '-' : status === 'NO_IDEAS' ? '--' : '!'
                      }
                    >
                      {status === 'EASY' ? '++' : status === 'OK_NO_PEEK' ? '+' : status === 'OK_WITH_PEEK' ? '+-' :
                        status === 'OK_WITH_QUESTION' ? '+?' : status === 'FAILED' ? '-' : status === 'NO_IDEAS' ? '--' : '!'}
                    </button>
                  ))}
                </div>
                {sol.self_comment && (
                  <div className="solution-self-comment text-secondary">
                    {sol.self_comment}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );

  const editorSlot = (
    <>
      <h3>Решение</h3>
      <CodeEditor
        value={code}
        onChange={setCode}
        height="400px"
      />
      <TestRunner
        code={code}
        taskId={task.id}
        testCases={visibleTests}
      />
    </>
  );

  return (
    <div className="task-detail-page">
      <TaskWorkLayout
        header={header}
        statement={statement}
        hintsSection={hintsSection}
        editorSlot={editorSlot}
        sidebarSlot={solutionsSidebar}
      />
    </div>
  );
}
